import { writeBatch, DocumentReference, DocumentData } from 'firebase/firestore';
import { db } from '../google/firebase';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { ApolloClient, HttpLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { GRAPHQL_API_ENDPOINT, NODE_ENV } from '../../env';
import { AuthHeader, getAuthHeaderForUser, handleAuthError } from '../auth/auth.util';

// Basic URL link for v1 API client.
const http_link = new HttpLink({ uri: GRAPHQL_API_ENDPOINT });

// v1 API link with appended authentication header for currently logged in user.
const auth_link = setContext(async (_, { headers }) => {
  const auth_header: AuthHeader = await getAuthHeaderForUser();
  return {
    headers: {
      ...headers,
      ...auth_header
    }
  };
});

// Error handling link for 401 responses.
const error_link = onError(({ networkError }) => {
  // Handle network errors (HTTP status codes).
  if (networkError) {
    handleAuthError(networkError);
  }
});

export const apollo_client = new ApolloClient({
  link: from([error_link, auth_link, http_link]),
  cache: new InMemoryCache(),
  devtools: {
    enabled: NODE_ENV !== 'production' ? true : false
  }
});

export interface BatchWrite {
  id: string;
  reference: DocumentReference;
  operation: 'create' | 'update' | 'delete';
  data: DocumentData;
}

/**
 * Generic function to write data to firestore documents in batched calls.
 * @param {Array<BatchWrite>} writes List of create, update or delete writes to execute with relative data
 * @returns {void}
 */
export const processBatchWrites = async (writes: Array<BatchWrite>) => {
  const batch_writes: Array<Promise<void>> = [];
  let batch = writeBatch(db);
  try {
    let operation_counter = 0;
    for (const write of writes) {
      if (write.operation === 'create') {
        batch.set(write.reference, write.data);
      } else {
        batch.update(
          write.reference,
          write.operation === 'delete' ? { deleted: new Date() } : write.data
        );
      }
      operation_counter++;
      if (operation_counter === 499) {
        batch_writes.push(batch.commit());
        batch = writeBatch(db);
        operation_counter = 0;
      }
    }
    if (operation_counter % 499 !== 0) {
      batch_writes.push(batch.commit());
    }
    await Promise.all(batch_writes);
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    log(
      'error',
      new MetadataError(
        err instanceof Error
          ? err.message
          : 'Error: lib/app/db_util.ts failed on an unknown error while calling processBatchWrites.',
        {
          writes: writes
        },
        tracking_id
      )
    );
  }
};
